import { Chart } from "@antv/g2";
import { uniqueId } from "@antv/util";
import { Empty, Select, Typography, theme } from "antd";
import { useEffect, useRef, useState } from "react";
import "../../styles/trend-wrapper.css";

const { Text, Title } = Typography;

function CustomLineChart({
  data,
  tooltipValueFormatter = (value, unit) =>
    `${value?.toLocaleString() ?? "-"} ${unit ?? ""}`,
  height = 400,
}) {
  const { token } = theme.useToken();

  const container = uniqueId("line-");
  const chartRef = useRef(null);
  const tooltipRef = useRef(null);
  const [activeTooltipTitle, setActiveTooltipTitle] = useState("-");
  const [activeSeriesList, setActiveSeriesList] = useState([]);
  const [chartAxisGroup, setChartAxisGroup] = useState("unit");

  const changeActiveSeries = (activeSeries) => {
    let list = [];
    if (!activeSeriesList?.includes(activeSeries)) {
      list = [...activeSeriesList, activeSeries];
    } else {
      list = activeSeriesList?.filter((s) => s !== activeSeries);
    }
    setActiveSeriesList(list);
    const chart = chartRef?.current;

    // chart.emit("legend:filter", {
    //   data: {
    //     channel: "color",
    //     values: [...new Set(data?.map((d) => d.series))].filter(
    //       (d) => !list.includes(d)
    //     ),
    //   },
    // });

    // Create a Set for `list` for faster lookups
    const listSet = new Set(list);

    // Create a Set for unique series values directly and filter simultaneously
    const filteredValues = Array.from(
      data.reduce((acc, d) => {
        if (!listSet.has(d.series)) {
          acc.add(d.series);
        }
        return acc;
      }, new Set())
    );

    // Emit the filtered legend event
    chart.emit("legend:filter", {
      data: {
        channel: "color",
        values: filteredValues,
      },
    });
  };

  const plotChart = () => {
    chartRef.current.clear();

    let group = Object.groupBy(data, (d) => d[chartAxisGroup]);

    // temporarily solution to the empty group.
    // TODO: make backend always return the key although the data is an empty array. and show "No data icon when it is empty"
    if (Object.keys(group).length === 0) {
      group = {
        _: [],
      };
    }

    Object.keys(group)?.forEach((key, index) => {
      chartRef.current
        .line()
        .data({
          type: "inline",
          value: group[key],
        })
        .encode("x", "time")
        .encode("y", (d) => d.value)
        .encode("color", "series")
        .slider("x", {
          style: {
            handleLabelFill: token?.colorTextBase,
          },
        })
        .scale("y", { independent: true })
        .axis("x", {
          line: true,
          lineStroke: token?.colorTextBase,
          tickStroke: token?.colorTextBase,
          title: "time",
          titleFill: token?.colorTextBase,
          labelFill: token?.colorTextBase,
        })
        .axis("y", {
          line: true,
          lineStroke: token?.colorTextBase,
          tickStroke: token?.colorTextBase,
          title: key,
          titleFill: token?.colorTextBase,
          labelFill: token?.colorTextBase,
        })
        .interaction("legendFilter", true);
    });
    chartRef.current.legend("color", {
      itemMarkerFillOpacity: 0,
      itemMarkerStrokeOpacity: 0,
      itemLabelFillOpacity: 0,
      itemValueFillOpacity: 0,
      itemBackgroundFillOpacity: 0,
    });
    chartRef.current.interaction("tooltip", {
      body: false,
      shared: true,
      crosshairs: true,
      crosshairsStroke: token?.colorTextBase,
    });
    chartRef.current.on("tooltip:show", (evt) => {
      const { xScale } = chartRef.current.getScaleByChannel("y").getOptions();
      const title =
        xScale?.sortedDomain[
          xScale?.sortedDomain.indexOf(evt?.data?.data?.x) + 1
        ];
      setActiveTooltipTitle(title);
    });

    chartRef.current.on("legend:filter", (e) => {
      const { nativeEvent, data } = e;
      if (!nativeEvent) return;
    });

    chartRef.current.render().then((chart) =>
      chart.emit("tooltip:show", {
        data: {
          data: { time: activeTooltipTitle },
        },
      })
    );
  };

  const generateTooltip = ({ token }) => {
    const chart = chartRef?.current;
    if (!chart) {
      return;
    }
    const scale = chartRef?.current.getScaleByChannel("color");
    const { domain, range } = scale.getOptions();

    return (
      <legend
        key={token?.colorTextBase}
        className="g2-tooltip"
        style={{ color: token?.colorTextBase }}
      >
        <div
          className="g2-tooltip-title"
          style={{ color: token?.colorTextBase }}
        >
          {activeTooltipTitle}
        </div>
        <div
          className="g2-tooltip-items"
          ref={tooltipRef}
          style={{ color: token?.colorTextBase }}
        >
          {domain?.map((value, idx) => {
            const onClick = () => changeActiveSeries(value);
            const item = data.filter((d) => {
              if (d.time)
                return d.time == activeTooltipTitle && d.series == value;
            });

            return (
              <div
                key={idx}
                className={`g2-tooltip-item tooltip-${value} ${
                  activeSeriesList?.includes(value) ? "inactive" : ""
                }`}
                onClick={onClick}
              >
                <div
                  className="g2-tooltip-item-marker"
                  style={{ background: range[idx] }}
                ></div>
                <div className="g2-tooltip-item-label">
                  <Typography.Text ellipsis={{ tooltip: value }}>
                    {value}
                  </Typography.Text>
                </div>
                <div className="g2-tooltip-item-value">
                  <Typography.Text
                    style={{ fontSize: 18 }}
                    ellipsis={{
                      tooltip: tooltipValueFormatter(
                        item[0]?.value,
                        item[0]?.unit
                      ),
                    }}
                  >
                    {tooltipValueFormatter(item[0]?.value, item[0]?.unit)}
                  </Typography.Text>
                </div>
              </div>
            );
          })}
        </div>
      </legend>
    );
  };

  useEffect(() => {
    chartRef.current = new Chart({
      container: container,
      autoFit: true,
    });

    plotChart();
    return () => {
      chartRef?.current.destroy();
    };
  }, []);

  useEffect(() => {
    const scrollContainer = tooltipRef.current;

    let startX = 0;
    let scrollLeft = 0;
    let isDragging = false;

    // Mouse drag start
    const handleMouseDown = (e) => {
      isDragging = true;
      startX = e.pageX - scrollContainer.offsetLeft; // Get the initial mouse position
      scrollLeft = scrollContainer.scrollLeft; // Store the current scroll position
    };

    // Mouse drag move
    const handleMouseMove = (e) => {
      if (!isDragging) return; // Only execute if dragging
      const moveX = e.pageX - scrollContainer.offsetLeft - startX; // Calculate the distance moved
      scrollContainer.scrollLeft = scrollLeft - moveX; // Adjust the scroll position based on the movement
    };

    // Mouse drag end
    const handleMouseUp = () => {
      isDragging = false; // Stop dragging
    };

    // Mouse leaves the scroll container (end dragging)
    const handleMouseLeave = () => {
      isDragging = false; // Stop dragging
    };

    // Mobile touch start
    const handleTouchStart = (e) => {
      startX = e.touches[0].pageX; // Get the initial touch position
      scrollLeft = scrollContainer.scrollLeft; // Store the current scroll position
    };

    // Mobile touch move (dragging)
    const handleTouchMove = (e) => {
      const moveX = e.touches[0].pageX - startX; // Calculate the distance moved
      scrollContainer.scrollLeft = scrollLeft - moveX; // Adjust the scroll position based on the movement
    };

    const handleScroll = (evt) => {
      if (evt.deltaY === 0) return; // Skip if there's no vertical scroll

      evt.preventDefault(); // Prevent vertical scroll
      scrollContainer.scrollLeft += evt.deltaY; // Scroll horizontally
    };

    if (scrollContainer) {
      scrollContainer.addEventListener("wheel", handleScroll);
      scrollContainer.addEventListener("touchstart", handleTouchStart);
      scrollContainer.addEventListener("touchmove", handleTouchMove);

      // Mouse events for drag
      scrollContainer.addEventListener("mousedown", handleMouseDown);
      scrollContainer.addEventListener("mousemove", handleMouseMove);
      scrollContainer.addEventListener("mouseup", handleMouseUp);
      scrollContainer.addEventListener("mouseleave", handleMouseLeave);
    }

    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener("wheel", handleScroll);
        scrollContainer.removeEventListener("touchstart", handleTouchStart);
        scrollContainer.removeEventListener("touchmove", handleTouchMove);

        // Remove mouse event listeners
        scrollContainer.removeEventListener("mousedown", handleMouseDown);
        scrollContainer.removeEventListener("mousemove", handleMouseMove);
        scrollContainer.removeEventListener("mouseup", handleMouseUp);
        scrollContainer.removeEventListener("mouseleave", handleMouseLeave);
      }
    };
  }, [tooltipRef.current]);

  useEffect(() => {
    setActiveSeriesList([]);
    if (!(data == null || data?.length === 0)) {
      plotChart();
    }
  }, [chartAxisGroup, data, token]);

  return (
    <div style={{ width: "100%", height: height }}>
      <section className={"wrapper trend-wrapper"}>
        <div
          style={{
            position: "absolute",
            top: "100px",
            left: "10px",
            width: 150,
            zIndex: 100,
          }}
        >
          <Text>Group :</Text>
          <Select
            style={{ width: 80 }}
            className="ml-2"
            value={chartAxisGroup}
            onSelect={(value) => setChartAxisGroup(value)}
            options={[
              { value: "unit", label: "Unit" },
              { value: "series", label: "Series" },
            ]}
          ></Select>
        </div>
        {chartRef?.current ? generateTooltip(token) : <></>}
        <div
          id={container}
          className={"chart-wrapper"}
          style={{ height: "calc(100% - 88px)", paddingTop: 15 }}
        ></div>
      </section>
    </div>
  );
}

export default CustomLineChart;
